import React, { useEffect, useState } from 'react';
import { ScreenSkeleton } from '../../components/atoms/ScreenSkeleton';
import { Label } from '../atoms/Label';
import { IJWTInfo } from '../../types/userJWTInfo';
import { parseJwt } from '../../utils/jwtParser';
import { tracker } from '../../services/tracker';
import { getDeviceType } from '../../utils/getDeviceType';
import packageJson from '../../../package.json';
import AzureTokenPayloadService from '../../services/azureTokenPayloadService';

interface SlackMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (message: string, meta: Record<string, any>) => Promise<boolean>;
  metadata?: Record<string, any>;
}

export const SlackMessageModal: React.FC<SlackMessageModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  metadata = {},
}) => {
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const [isLoading, setIsLoading] = useState(true);

  const [messageSent, setMessageSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get name and email from Azure Token if available
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        let fullName = '';
        let email = '';

        if (token) {
          // Use the unified service to get full name with fallback logic
          try {
            fullName = AzureTokenPayloadService.getFullNameOfUser(token);
          } catch (e) {
            console.warn('Could not extract full name from token:', e);
            // Continue with empty name rather than failing entirely
          }

          // Parse JWT for email (wrapped in try/catch)
          try {
            const info: IJWTInfo = parseJwt(token);
            email = info.email || '';
          } catch (e) {
            console.warn('Could not parse JWT for email:', e);
            // Continue with empty email rather than failing entirely
          }
        } else {
          console.warn('No JWT token found.');
        }

        // Set user data (even if empty)
        setName(fullName);
        setEmail(email);
        metadata.userName = fullName;
        metadata.userEmail = email;

        // Add application context metadata
        metadata.applicationVersion =
          import.meta.env.VITE_APP_VERSION || packageJson.version || 'unknown';
        metadata.environment = import.meta.env.VITE_API_URL?.includes(
          'localhost',
        )
          ? 'development'
          : 'production';
        metadata.timestamp = new Date().toISOString();
        metadata.userTimeZone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Add device information metadata
        metadata.deviceInfo = {
          userAgent: navigator.userAgent,
          screenResolution: `${screen.width}x${screen.height}`,
          deviceType: getDeviceType(),
          platform: navigator.platform,
          language: navigator.language,
          cookieEnabled: navigator.cookieEnabled,
          online: navigator.onLine,
        };

        // Add network and performance context
        metadata.networkStatus = navigator.onLine ? 'online' : 'offline';
        const connection = (navigator as any).connection;
        if (connection) {
          metadata.connectionType = connection.effectiveType || 'unknown';
          metadata.networkDownlink = connection.downlink;
        }

        setIsLoading(false);
      } catch (e) {
        console.error('Failed to fetch user data:', e);
        setError('Failed to load user information.');
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const handleSend = async () => {
    const response = await onSubmit(message, metadata);
    if (!response) {
      setError('Failed to send message to Slack. Please try again.');
      return;
    }
    setError(null); // Reset error state
    setMessageSent(true);
    onClose();
  };

  if (messageSent) {
    return (
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <Label className="mb-4 text-lg font-bold">Message Sent</Label>
        <p className="text-ink">Your message has been sent.</p>
        <Label className="mt-2 text-sm text-inkMuted">"{message}"</Label>
      </div>
    );
  }

  if (isLoading) {
    return <ScreenSkeleton fields={4} />;
  }

  return (
    <>
      {/* <div className="flex items-center justify-center min-h-screen px-4"> */}
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {/* Add Name and email address fields */}
        <Label className="text-xl font-bold">Name</Label>
        <input
          className="mt-4 h-12 w-full resize-none rounded-lg border border-line p-3 focus:ring-2 focus:ring-brand focus:outline-none"
          placeholder="Your Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <Label className="mt-4 text-xl font-bold">Email Address</Label>
        <input
          type="text"
          className="mt-4 h-12 w-full rounded-lg border border-line p-3 focus:ring-2 focus:ring-brand focus:outline-none"
          placeholder="Your Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Label className="my-4 text-xl font-bold">Additional Information</Label>
        <textarea
          className="h-32 w-full resize-none rounded-lg border border-line p-3 focus:ring-2 focus:ring-brand focus:outline-none"
          placeholder="Write your message here..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <Label className="text-red-400">{error}</Label>
        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={handleSend}
            className="rounded-lg bg-primaryColor px-4 py-2 text-white hover:bg-brand"
          >
            Send
          </button>
        </div>
      </div>
      {/* </div> */}
    </>
  );
};
